/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { identityProviderConnectionsSubmitClientId } from "../funcs/identityProviderConnectionsSubmitClientId.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { OktaIdentityProviderConnection } from "../models/components/oktaidentityproviderconnection.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SubmitIdentityProviderConnectionClientIdRequest,
  SubmitIdentityProviderConnectionClientIdSecurity,
} from "../models/operations/submitidentityproviderconnectionclientid.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SubmitIdentityProviderConnectionClientIdMutationVariables = {
  request: SubmitIdentityProviderConnectionClientIdRequest;
  security?: SubmitIdentityProviderConnectionClientIdSecurity | undefined;
  options?: RequestOptions;
};

export type SubmitIdentityProviderConnectionClientIdMutationData =
  OktaIdentityProviderConnection;

export type SubmitIdentityProviderConnectionClientIdMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * submitClientId identityProviderConnections
 *
 * @remarks
 * Record the client ID of the Okta API Services application and verify it. Connections installed from the Okta Integration Network also take the client secret. Allowed once, while the connection is pending; revoke and recreate to change it. Requires org:admin.
 */
export function useSubmitIdentityProviderConnectionClientIdMutation(
  options?: MutationHookOptions<
    SubmitIdentityProviderConnectionClientIdMutationData,
    SubmitIdentityProviderConnectionClientIdMutationError,
    SubmitIdentityProviderConnectionClientIdMutationVariables
  >,
): UseMutationResult<
  SubmitIdentityProviderConnectionClientIdMutationData,
  SubmitIdentityProviderConnectionClientIdMutationError,
  SubmitIdentityProviderConnectionClientIdMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSubmitIdentityProviderConnectionClientIdMutation(client, options),
    ...options,
  });
}

export function mutationKeySubmitIdentityProviderConnectionClientId(): MutationKey {
  return ["@gram/client", "identityProviderConnections", "submitClientId"];
}

export function buildSubmitIdentityProviderConnectionClientIdMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SubmitIdentityProviderConnectionClientIdMutationVariables,
  ) => Promise<SubmitIdentityProviderConnectionClientIdMutationData>;
} {
  return {
    mutationKey: mutationKeySubmitIdentityProviderConnectionClientId(),
    mutationFn: function submitIdentityProviderConnectionClientIdMutationFn({
      request,
      security,
      options,
    }): Promise<SubmitIdentityProviderConnectionClientIdMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(identityProviderConnectionsSubmitClientId(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
